/**
 * Promo Code Management Page (Admin Only)
 *
 * Two tabs:
 * - "Promo Codes" — CRUD for promo codes
 * - "Usages" — who used which code and when
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Pagination } from '../../components/ui/Pagination';
import {
  adminListPromoCodes,
  adminCreatePromoCode,
  adminUpdatePromoCode,
  adminDeletePromoCode,
  adminTogglePromoCode,
  adminListPromoCodeUsages,
  type PromoCode,
  type PromoCodeUsage,
  type CreatePromoCodeData,
} from '../../services/promoCodeService';

type TabType = 'codes' | 'usages';

export const PromoCodeManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<TabType>('codes');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('promo_codes.page_title')}</h1>
        <p className="text-gray-600 mt-1">{t('promo_codes.page_subtitle')}</p>
      </div>

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('codes')}
            className={`${
              activeTab === 'codes'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            {t('promo_codes.tab_codes')}
          </button>
          <button
            onClick={() => setActiveTab('usages')}
            className={`${
              activeTab === 'usages'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            {t('promo_codes.tab_usages')}
          </button>
        </nav>
      </div>

      {activeTab === 'codes' ? (
        <PromoCodesTab setSuccess={setSuccess} setError={setError} t={t} />
      ) : (
        <UsagesTab setError={setError} t={t} />
      )}
    </div>
  );
};

// ── Promo Codes Tab ───────────────────────────────────────────────────────────

interface CodesTabProps {
  setSuccess: (msg: string | null) => void;
  setError: (msg: string | null) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function PromoCodesTab({ setSuccess, setError, t }: CodesTabProps) {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    codeId: number | null;
    codeText: string | null;
  }>({ open: false, codeId: null, codeText: null });

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const res = await adminListPromoCodes({ page: currentPage, search: search || undefined });
      setCodes(res.data);
      setTotalPages(res.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('promo_codes.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [currentPage, search]);

  const handleDelete = (id: number, code: string) => {
    setDeleteConfirm({ open: true, codeId: id, codeText: code });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.codeId) return;
    try {
      await adminDeletePromoCode(deleteConfirm.codeId);
      setSuccess(t('promo_codes.messages.deleted'));
      fetchCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('promo_codes.messages.delete_error'));
    }
  };

  const handleToggle = async (code: PromoCode) => {
    try {
      await adminTogglePromoCode(code.id);
      setSuccess(
        code.disabled_at
          ? t('promo_codes.messages.enabled')
          : t('promo_codes.messages.disabled')
      );
      fetchCodes();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('promo_codes.messages.toggle_error'));
    }
  };

  const getCodeStatus = (code: PromoCode): { label: string; variant: 'success' | 'warning' | 'danger' | 'default' } => {
    if (code.disabled_at) {
      return { label: t('promo_codes.status_disabled'), variant: 'danger' };
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return { label: t('promo_codes.status_expired'), variant: 'warning' };
    }
    if (code.max_uses !== null && code.uses_count >= code.max_uses) {
      return { label: t('promo_codes.status_exhausted'), variant: 'warning' };
    }
    return { label: t('promo_codes.status_active'), variant: 'success' };
  };

  return (
    <>
      <Card>
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="text"
              placeholder={t('promo_codes.search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <Button
            onClick={() => {
              setEditingCode(null);
              setShowModal(true);
            }}
          >
            {t('promo_codes.create_button')}
          </Button>
        </div>

        {loading ? (
          <div className="text-gray-500 text-sm py-8 text-center">{t('promo_codes.loading')}</div>
        ) : codes.length === 0 ? (
          <div className="text-gray-500 text-sm py-8 text-center">{t('promo_codes.no_codes')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_code')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_discount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_uses')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_expires')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('promo_codes.col_actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {codes.map((code) => {
                  const status = getCodeStatus(code);
                  return (
                    <tr key={code.id}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-mono font-semibold text-gray-900">{code.code}</div>
                        {code.description && (
                          <div className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{code.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {parseFloat(code.discount_percent)}%
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {code.uses_count}
                        {code.max_uses !== null ? ` / ${code.max_uses}` : ' / ∞'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {code.expires_at
                          ? format(new Date(code.expires_at), 'MMM d, yyyy')
                          : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingCode(code);
                              setShowModal(true);
                            }}
                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            {t('promo_codes.action_edit')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(code)}
                            className={`text-xs px-2 py-1 rounded border ${
                              code.disabled_at
                                ? 'border-green-300 text-green-700 hover:bg-green-50'
                                : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                            }`}
                          >
                            {code.disabled_at
                              ? t('promo_codes.action_enable')
                              : t('promo_codes.action_disable')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(code.id, code.code)}
                            className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                          >
                            {t('promo_codes.action_delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </Card>

      {showModal && (
        <PromoCodeFormModal
          code={editingCode}
          onClose={() => {
            setShowModal(false);
            setEditingCode(null);
          }}
          onSuccess={(msg) => {
            setSuccess(msg);
            setShowModal(false);
            setEditingCode(null);
            fetchCodes();
          }}
          onError={setError}
          t={t}
        />
      )}

      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, codeId: null, codeText: null })}
        onConfirm={confirmDelete}
        title={t('promo_codes.action_delete')}
        message={t('promo_codes.delete_confirm_message')}
        description={deleteConfirm.codeText ?? undefined}
        confirmText={t('promo_codes.action_delete')}
        variant="danger"
      />
    </>
  );
}

// ── Usages Tab ────────────────────────────────────────────────────────────────

interface UsagesTabProps {
  setError: (msg: string | null) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function UsagesTab({ setError, t }: UsagesTabProps) {
  const [usages, setUsages] = useState<PromoCodeUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'used' | 'expired' | ''>('');
  const [search, setSearch] = useState('');

  const fetchUsages = async () => {
    try {
      setLoading(true);
      const res = await adminListPromoCodeUsages({
        page: currentPage,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setUsages(res.data);
      setTotalPages(res.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('promo_codes.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsages();
  }, [currentPage, statusFilter, search]);

  const getUsageStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'default' => {
    if (status === 'used') return 'success';
    if (status === 'pending') return 'warning';
    return 'danger';
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <Input
            type="text"
            placeholder={t('promo_codes.search_placeholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter);
            setCurrentPage(1);
          }}
          className="input w-40"
        >
          <option value="">{t('promo_codes.filter_all')}</option>
          <option value="pending">{t('promo_codes.status_usage_pending')}</option>
          <option value="used">{t('promo_codes.status_usage_used')}</option>
          <option value="expired">{t('promo_codes.status_usage_expired')}</option>
        </select>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">{t('promo_codes.loading')}</div>
      ) : usages.length === 0 ? (
        <div className="text-gray-500 text-sm py-8 text-center">{t('promo_codes.no_usages')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_code')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_user')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_account')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_plan')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_original')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_discounted')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_saved')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_reserved_at')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('promo_codes.usage_col_used_at')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usages.map((usage) => {
                const originalAmt = parseFloat(usage.original_amount);
                const discountedAmt = parseFloat(usage.discounted_amount);
                const savedAmt = originalAmt - discountedAmt;
                return (
                  <tr
                    key={usage.id}
                    className={
                      usage.status === 'pending'
                        ? 'bg-yellow-50'
                        : usage.status === 'used'
                        ? 'bg-green-50'
                        : ''
                    }
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono text-sm font-semibold text-gray-900">
                        {usage.promo_code?.code ?? '—'}
                      </span>
                      <div className="text-xs text-gray-500">{parseFloat(usage.discount_percent)}% off</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <div>{usage.user?.name ?? '—'}</div>
                      <div className="text-xs text-gray-500">{usage.user?.email ?? ''}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {usage.account?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {usage.plan?.name ?? '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      ${originalAmt.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-700">
                      ${discountedAmt.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-red-600">
                      -${savedAmt.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={getUsageStatusVariant(usage.status)}>
                        {t(`promo_codes.status_usage_${usage.status}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {format(new Date(usage.reserved_at), 'MMM d, yyyy HH:mm')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {usage.used_at ? format(new Date(usage.used_at), 'MMM d, yyyy HH:mm') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Card>
  );
}

// ── Promo Code Form Modal ─────────────────────────────────────────────────────

interface PromoCodeFormModalProps {
  code: PromoCode | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function PromoCodeFormModal({ code, onClose, onSuccess, onError, t }: PromoCodeFormModalProps) {
  const [formData, setFormData] = useState<CreatePromoCodeData>({
    code: code?.code ?? '',
    description: code?.description ?? '',
    discount_percent: code ? parseFloat(code.discount_percent) : 10,
    max_uses: code?.max_uses ?? null,
    expires_at: code?.expires_at
      ? code.expires_at.substring(0, 10)
      : null,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: CreatePromoCodeData = {
        code: formData.code.toUpperCase(),
        description: formData.description || undefined,
        discount_percent: formData.discount_percent,
        max_uses: formData.max_uses ?? undefined,
        expires_at: formData.expires_at || null,
      };

      if (code) {
        await adminUpdatePromoCode(code.id, payload);
        onSuccess(t('promo_codes.messages.updated'));
      } else {
        await adminCreatePromoCode(payload);
        onSuccess(t('promo_codes.messages.created'));
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : t('promo_codes.messages.save_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={code ? t('promo_codes.modal_edit_title') : t('promo_codes.modal_create_title')}
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Code */}
        <div>
          <label className="label">
            {t('promo_codes.field_code')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            placeholder="LAUNCH20"
            required
            maxLength={20}
            className="font-mono uppercase"
          />
          <p className="text-xs text-gray-500 mt-1">{t('promo_codes.field_code_hint')}</p>
        </div>

        {/* Description */}
        <div>
          <label className="label">{t('promo_codes.field_description')}</label>
          <Input
            type="text"
            value={formData.description ?? ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('promo_codes.field_description_placeholder')}
          />
        </div>

        {/* Discount Percent */}
        <div>
          <label className="label">
            {t('promo_codes.field_discount_percent')} <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            value={formData.discount_percent}
            onChange={(e) => setFormData({ ...formData, discount_percent: Number(e.target.value) })}
            min={1}
            max={100}
            step={0.01}
            required
          />
        </div>

        {/* Max Uses */}
        <div>
          <label className="label">{t('promo_codes.field_max_uses')}</label>
          <Input
            type="number"
            value={formData.max_uses ?? ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                max_uses: e.target.value ? Number(e.target.value) : null,
              })
            }
            min={1}
            placeholder={t('promo_codes.field_max_uses_placeholder')}
          />
          <p className="text-xs text-gray-500 mt-1">{t('promo_codes.field_max_uses_hint')}</p>
        </div>

        {/* Expires At */}
        <div>
          <label className="label">{t('promo_codes.field_expires_at')}</label>
          <Input
            type="date"
            value={formData.expires_at ?? ''}
            onChange={(e) =>
              setFormData({ ...formData, expires_at: e.target.value || null })
            }
          />
          <p className="text-xs text-gray-500 mt-1">{t('promo_codes.field_expires_hint')}</p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('promo_codes.action_cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('promo_codes.saving') : t('promo_codes.action_save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
